/* global Word, console */
import { useState } from "react";
import { ReviewTileItem, SearchResult } from "@/types/tile.types";
import { useFuzzySearch } from "./useFuzzySearch";
import { useTileTextExtraction } from "./useTileTextExtraction";

export interface UseTileSearchOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  onSearchComplete?: (results: SearchResult[]) => void;
  highlightInDocument?: boolean;
  caseSensitive?: boolean;
  wholeWords?: boolean;
}

export const useTileSearch = (options?: UseTileSearchOptions) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const { fuzzySearchInDocument } = useFuzzySearch();
  const { getSearchableText, normalizeSearchText } = useTileTextExtraction();

  const normalizeText = (text: string): string => {
    if (!options?.caseSensitive) {
      text = text.toLowerCase();
    }
    return text.trim();
  };

  const findMatches = (
    text: string,
    searchTerm: string
  ): { startIndex: number; endIndex: number }[] => {
    const matches: { startIndex: number; endIndex: number }[] = [];
    const normalizedText = normalizeText(text);
    const normalizedTerm = normalizeText(searchTerm);

    if (options?.wholeWords) {
      const regex = new RegExp(`\\b${normalizedTerm}\\b`, options?.caseSensitive ? "g" : "gi");
      let match;
      while ((match = regex.exec(normalizedText)) !== null) {
        matches.push({
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      }
    } else {
      let startIndex = 0;
      while (true) {
        const index = normalizedText.indexOf(normalizedTerm, startIndex);
        if (index === -1) break;

        matches.push({
          startIndex: index,
          endIndex: index + normalizedTerm.length,
        });

        startIndex = index + 1;
      }
    }

    return matches;
  };

  const searchTiles = async (
    tiles: ReviewTileItem[],
    searchTerm: string
  ): Promise<SearchResult[]> => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return [];
    }

    setIsSearching(true);
    setCurrentSearchTerm(searchTerm);

    // Add to search history
    setSearchHistory((prev) => {
      const updated = [searchTerm, ...prev.filter((term) => term !== searchTerm)];
      return updated.slice(0, 10); // Keep last 10 searches
    });

    try {
      const results: SearchResult[] = [];

      for (const tile of tiles) {
        const tileMatches: SearchResult["matches"] = [];
        let totalMatches = 0;

        // Search in different fields
        const fieldsToSearch: (keyof ReviewTileItem)[] = [
          "clause",
          "reason",
          "analysis",
          "explanation",
        ];

        for (const field of fieldsToSearch) {
          const fieldValue = tile[field];
          if (typeof fieldValue === "string" && fieldValue) {
            const matches = findMatches(fieldValue, searchTerm);
            if (matches.length > 0) {
              tileMatches.push({
                field,
                text: fieldValue,
                startIndex: matches[0]?.startIndex || 0,
                endIndex: matches[0]?.endIndex || 0,
              });
              totalMatches += matches.length;
            }
          }
        }

        if (tileMatches.length > 0) {
          // Calculate relevance score
          let score = totalMatches;

          // Boost score for exact matches in clause
          if (tile.clause && normalizeText(tile.clause).includes(normalizeText(searchTerm))) {
            score += 10;
          }

          // Boost score for matches at the beginning of text
          tileMatches.forEach((match) => {
            if (match.startIndex === 0) {
              score += 5;
            }
          });

          results.push({
            tile,
            matches: tileMatches,
            score,
          });
        }
      }

      // Sort by score (highest first)
      results.sort((a, b) => b.score - a.score);

      setSearchResults(results);
      options?.onSearchComplete?.(results);
      options?.onSuccess?.(`Found ${results.length} tiles matching "${searchTerm}"`);

      return results;
    } catch (error: any) {
      console.error("Error searching tiles:", error);
      options?.onError?.(`Search failed: ${error.message}`);
      return [];
    } finally {
      setIsSearching(false);
    }
  };



  const searchInDocumentWithTile = async (
    item: ReviewTileItem,
    selectedOptionsForReview?: Array<{ name: string; code: string }>
  ): Promise<boolean> => {
    try {
      const searchableText = getSearchableText(item, selectedOptionsForReview);
      if (!searchableText || typeof searchableText !== "string") {
        options?.onError?.("No searchable text found for this tile");
        return false;
      }

      const normalizedText = normalizeSearchText(searchableText);
      if (!normalizedText) {
        options?.onError?.("No valid search text after normalization");
        return false;
      }



      // Truncate to 255 characters to avoid Word API errors
      let truncatedText = normalizedText;
      if (truncatedText.length > 255) {
        truncatedText = truncatedText.substring(0, 255);
      }

      // Try full text search first with truncated text
      const fullTextResult = await searchInDocument(truncatedText);

      if (fullTextResult) {
        return true;
      }

      // Try with delimiters if full text fails
      const delimiters = [":", ";"];
      for (const delimiter of delimiters) {
        if (truncatedText.includes(delimiter)) {
          const parts = truncatedText.split(delimiter);
          const firstPart = parts[0]?.trim();
          if (firstPart) {

            const fallbackResult = await searchInDocument(firstPart);

            if (fallbackResult) {
              return true;
            }
          }
        }
      }


      // Try fuzzy search as last resort with FULL text
      const fuzzyResult = await fuzzySearchInDocument(normalizedText);
      if (fuzzyResult) {
        options?.onSuccess?.("Found match using fuzzy search");
        return true;
      }

      return false;
    } catch (error: any) {
      console.error("Error in searchInDocumentWithTile:", error);
      options?.onError?.(`Search failed: ${error.message}`);
      return false;
    }
  };

  const searchInDocument = async (searchTerm: string): Promise<boolean> => {
    if (!searchTerm.trim()) {
      options?.onError?.("Search term is required");
      return false;
    }

    try {
      return await Word.run(async (context) => {
        const searchOptions = {
          ignorePunct: true,
          ignoreSpace: true,
          matchCase: options?.caseSensitive || false,
          matchWholeWord: options?.wholeWords || false,
        };

        const searchResults = context.document.body.search(searchTerm, searchOptions);
        context.load(searchResults, "items");

        await context.sync();



        if (searchResults.items.length > 0) {
          // Navigate to first result without highlighting
          searchResults.items[0]?.select();

          await context.sync();

          options?.onSuccess?.(`Found ${searchResults.items.length} matches in document`);
          return true;
        } else {
          // Don't show error here to allow fallback
          // options?.onError?.(`No matches found for "${searchTerm}" in document`);
          return false;
        }
      });
    } catch (error: any) {
      console.error("Error searching in document:", error);
      options?.onError?.(`Document search failed: ${error.message}`);
      return false;
    }
  };

  const clearHighlights = async (): Promise<boolean> => {
    try {
      await Word.run(async (context) => {
        const body = context.document.body;

        // Find all highlighted text
        const highlightedText = body.search("", {
          ignorePunct: true,
          ignoreSpace: true,
        });

        context.load(highlightedText, "items");
        await context.sync();

        // Remove highlights
        highlightedText.items.forEach((item) => {
          item.font.highlightColor = null as any;
          item.font.bold = false;
        });

        await context.sync();

        options?.onSuccess?.("Highlights cleared from document");
      });
      return true;
    } catch (error: any) {
      console.error("Error clearing highlights:", error);
      options?.onError?.(`Failed to clear highlights: ${error.message}`);
      return false;
    }
  };

  const searchByField = async (
    tiles: ReviewTileItem[],
    field: keyof ReviewTileItem,
    searchTerm: string
  ): Promise<SearchResult[]> => {
    setIsSearching(true);

    try {
      const results: SearchResult[] = [];

      for (const tile of tiles) {
        const fieldValue = tile[field];
        if (typeof fieldValue === "string" && fieldValue) {
          const matches = findMatches(fieldValue, searchTerm);
          if (matches.length > 0) {
            results.push({
              tile,
              matches: [
                {
                  field,
                  text: fieldValue,
                  startIndex: matches[0]?.startIndex || 0,
                  endIndex: matches[0]?.endIndex || 0,
                },
              ],
              score: matches.length,
            });
          }
        }
      }

      results.sort((a, b) => b.score - a.score);
      setSearchResults(results);

      options?.onSuccess?.(`Found ${results.length} tiles with "${searchTerm}" in ${field}`);
      return results;
    } catch (error: any) {
      console.error("Error searching by field:", error);
      options?.onError?.(`Field search failed: ${error.message}`);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const fuzzySearch = async (
    tiles: ReviewTileItem[],
    searchTerm: string
  ): Promise<SearchResult[]> => {
    // Simple fuzzy search implementation
    const fuzzyMatch = (text: string, term: string): number => {
      const normalizedText = normalizeText(text);
      const normalizedTerm = normalizeText(term);

      let termIndex = 0;

      for (let i = 0; i < normalizedText.length && termIndex < normalizedTerm.length; i++) {
        if (normalizedText[i] === normalizedTerm[termIndex]) {
          termIndex++;
        }
      }

      // Return percentage match
      return termIndex / normalizedTerm.length;
    };

    setIsSearching(true);

    try {
      const results: SearchResult[] = [];
      const minScore = 0.6; // Minimum 60% match

      for (const tile of tiles) {
        const fieldsToSearch: (keyof ReviewTileItem)[] = [
          "clause",
          "reason",
          "analysis",
          "explanation",
        ];
        let bestScore = 0;
        let bestMatches: SearchResult["matches"] = [];

        for (const field of fieldsToSearch) {
          const fieldValue = tile[field];
          if (typeof fieldValue === "string" && fieldValue) {
            const score = fuzzyMatch(fieldValue, searchTerm);
            if (score > bestScore && score >= minScore) {
              bestScore = score;
              bestMatches = [
                {
                  field,
                  text: fieldValue,
                  startIndex: 0,
                  endIndex: fieldValue.length,
                },
              ];
            }
          }
        }

        if (bestScore >= minScore) {
          results.push({
            tile,
            matches: bestMatches,
            score: bestScore * 100, // Convert to percentage
          });
        }
      }

      results.sort((a, b) => b.score - a.score);
      setSearchResults(results);

      options?.onSuccess?.(`Fuzzy search found ${results.length} potential matches`);
      return results;
    } catch (error: any) {
      console.error("Error in fuzzy search:", error);
      options?.onError?.(`Fuzzy search failed: ${error.message}`);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearchResults = (): void => {
    setSearchResults([]);
    setCurrentSearchTerm("");
  };

  const getSearchHistory = (): string[] => {
    return searchHistory;
  };

  const clearSearchHistory = (): void => {
    setSearchHistory([]);
  };

  return {
    isSearching,
    searchResults,
    currentSearchTerm,
    searchHistory,
    searchTiles,
    searchInDocument,
    searchInDocumentWithTile,
    clearHighlights,
    searchByField,
    fuzzySearch,
    clearSearchResults,
    getSearchHistory,
    clearSearchHistory,
  };
};
